import Request from "services";
import { Endpoints } from "services/endpoints";
import { handleRequestError } from "utils";
import {CreateStoreRequestBody, ProductsRequestParams, ProductsResponseData, StoresRequestParams, CreateProductsRequest, AddProductDeliveryLocationRequest, OrdersRequestParams, OrdersResponseData, UpdateProductsRequest, RefundOrderRequest, CreateDiscountRequest, DiscountsRequestParams, DiscountsResponseData, StoresResponseData } from "./types";
import { StoreStatus, Store, Product, Order, OrderStatusCount, ProductDiscountCode } from "interface/store";

export const createStore = async (data: CreateStoreRequestBody) => {
    try {
        const response = await Request.postWithUserToken<Store>(Endpoints.createStore, {data});
        return response;
    } catch (error) {
        handleRequestError(error);
    }
}

export const getStores = async (data: StoresRequestParams) => {
    try {
        const response = await Request.getWithUserToken<StoresResponseData>(Endpoints.getStores, {params: data});
        return response;
    } catch (error) {
        handleRequestError(error);
    }
}

export const updateStore = async (id: string, data: CreateStoreRequestBody) => {
    try {
        const response = await Request.putWithUserToken<Store>(Endpoints.updateStore(id), {data});
        return response;
    } catch (error) {
        handleRequestError(error);
    }
}

export const updateStoreStatus = async (id: string, status: StoreStatus) => {
    try {
        const response = await Request.putWithUserToken<Store>(Endpoints.updateStoreStatus(id), {params: {status}});
        return response;
    } catch (error) {
        handleRequestError(error);
    }
}

export const duplicateStore = async (id: string, data: CreateStoreRequestBody) => {
    try {
        const response = await Request.postWithUserToken<Store>(Endpoints.duplicateStore(id), {data});
        return response;
    } catch (error) {
        handleRequestError(error);
    }
}

export const getStore = async (id: string) => {
    try {
        const response = await Request.getWithUserToken<Store>(Endpoints.store(id));
        return response;
    } catch (error) {
        handleRequestError(error);
    }
}

export const deleteStore = async (id: string) => {
    try {
        const response = await Request.deleteWithUserToken<Store>(Endpoints.store(id));
        return response;
    } catch (error) {
        handleRequestError(error);
    }
}

export const getProducts = async (storId: string, param: ProductsRequestParams) => {
    try {
        const response = await Request.getWithUserToken<ProductsResponseData>(Endpoints.getProducts(storId), {params: param});
        return response;
    } catch (error) {
        handleRequestError(error);
    }
}

export const getProduct = async (productId: string) => {
    try {
        const response = await Request.getWithUserToken<Product>(Endpoints.getProduct(productId));
        return response;
    } catch (error) {
        handleRequestError(error);
    }
}

export const createProduct = async (data: CreateProductsRequest) => {
    try {
        const response = await Request.postWithUserToken<Product>(Endpoints.createProducts, {data});
        return response;
    } catch (error) {
        handleRequestError(error);
    }
}

export const duplicateProduct = async (productId: string, data: Partial<CreateProductsRequest> = {}) => {
    try {
        const response = await Request.postWithUserToken<Product>(Endpoints.duplicateProduct(productId), {data});
        return response;
    } catch (error) {
        handleRequestError(error);
    }
}

export const deleteProduct = async (productId: string) => {
    try {
        const response = await Request.deleteWithUserToken<Product>(Endpoints.deleteProduct(productId));
        return response;
    } catch (error) {
        handleRequestError(error);
    }
}

export const updateProduct = async (productId: string, data: UpdateProductsRequest) => {
    try {
        const response = await Request.putWithUserToken<Product>(Endpoints.updateProduct(productId), {data});
        return response;
    } catch (error) {
        handleRequestError(error);
    }
}


export const addDeliveryLocation = async (productId: string, data: AddProductDeliveryLocationRequest) => {
    try {
        const response = await Request.postWithUserToken<Product>(Endpoints.addDeliveryLocation(productId), {data});
        return response;
    } catch (error) {
        handleRequestError(error);
    }
}

export const updateDeliveryLocation = async (productId: string, data: AddProductDeliveryLocationRequest) => {
    try {
        const response = await Request.putWithUserToken<Product>(Endpoints.updateDeliveryLocation(productId), {data});
        return response;
    } catch (error) {
        handleRequestError(error);
    }
}

export const deleteDeliveryLocation = async (locationId: string) => {
    try {
        const response = await Request.deleteWithUserToken(Endpoints.deleteDeliveryLocation(locationId));
        return response;
    } catch (error) {
        handleRequestError(error);
    }
};

export const getOrders = async (params: OrdersRequestParams) => {
    try {
        const response = await Request.getWithUserToken<OrdersResponseData>(Endpoints.getOrders, {params});
        return response;
    } catch (error) {
        handleRequestError(error);
    }
};

export const getOrder = async (orderId: string) => {
    try {
        const response = await Request.getWithUserToken<Order>(Endpoints.getOrder(orderId));
        return response;
    } catch (error) {
        handleRequestError(error);
    }
};

export const getOrderCount = async () => {
    try {
        const response = await Request.getWithUserToken<OrderStatusCount>(Endpoints.orderCount);
        return response;
    } catch (error) {
        // handleRequestError(error);
    }
};

export const makeOrderDelivered = async (orderId: string) => {
    try {
        const response = await Request.putWithUserToken<OrderStatusCount>(Endpoints.markOrderDelivered(orderId));
        return response;
    } catch (error) {
        handleRequestError(error);
    }
};

export const cancelOrder = async (orderId: string) => {
    try {
        const response = await Request.deleteWithUserToken<OrderStatusCount>(Endpoints.cancelOrder(orderId));
        return response;
    } catch (error) {
        handleRequestError(error);
    }
};

export const refundOrder = async (orderId: string, data: RefundOrderRequest) => {
    try {
        const response = await Request.putWithUserToken<OrderStatusCount>(Endpoints.refundOrder(orderId), {data});
        return response;
    } catch (error) {
        handleRequestError(error);
    }
};

export const createDiscount = async (data: CreateDiscountRequest) => {
    try {
        const response = await Request.postWithUserToken<ProductDiscountCode>(Endpoints.createDiscount, {data});
        return response;
    } catch (error) {
        handleRequestError(error);
    }
};

export const getDiscounts = async (params: DiscountsRequestParams) => {
    try {
        const response = await Request.getWithUserToken<DiscountsResponseData>(Endpoints.discounts, {params});
        return response;
    } catch (error) {
        handleRequestError(error);
    }
};

export const setDiscountState = async (discountId: string, status?: 'ACTIVE' | 'INACTIVE') => {
    try {
        const response = await Request.putWithUserToken<DiscountsResponseData>(Endpoints.setDiscountState(discountId), {params: {status}});
        return response;
    } catch (error) {
        handleRequestError(error);
    }
};

export const getDiscount = async (discountId: string) => {
    try {
        const response = await Request.getWithUserToken<ProductDiscountCode>(Endpoints.discount(discountId));
        return response;
    } catch (error) {
        handleRequestError(error);
    }
};

export const updateDiscount = async (discountId: string, data: Partial<CreateDiscountRequest>) => {
    try {
        const response = await Request.putWithUserToken<DiscountsResponseData>(Endpoints.discount(discountId), {data});
        return response;
    } catch (error) {
        handleRequestError(error);
    }
};

export const deleteDiscount = async (discountId: string) => {
    try {
        const response = await Request.deleteWithUserToken<DiscountsResponseData>(Endpoints.discount(discountId));
        return response;
    } catch (error) {
        handleRequestError(error);
    }
};